import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import type { OrderDoc, PaginatedOrders } from './ordersSlice';

export interface EmployeeRecentTransactionsState {
  loading: boolean;
  error: string | null;
  recent: PaginatedOrders;
  lastFetchedAt: string | null;
}

const createInitialPage = (): PaginatedOrders => ({
  docs: [],
  hasNextPage: true,
  hasPrevPage: false,
  limit: 50,
  nextPage: 1,
  page: 1,
  perPage: 50,
  totalDocs: 0,
  totalPages: 0,
});

const initialState: EmployeeRecentTransactionsState = {
  loading: false,
  error: null,
  recent: createInitialPage(),
  lastFetchedAt: null,
};

const mergeOrders = (
  existing: OrderDoc[],
  incoming: OrderDoc[],
): OrderDoc[] => {
  const orderMap = new Map<string, OrderDoc>();
  [...existing, ...incoming].forEach(order => {
    const key = String(order?.bp_order_id || '');
    if (!key) return;
    const previous = orderMap.get(key);
    if (
      !previous ||
      new Date(order.bp_order_master_updated_at).getTime() >
        new Date(previous.bp_order_master_updated_at).getTime()
    ) {
      orderMap.set(key, { ...order });
    }
  });
  return Array.from(orderMap.values()).sort(
    (a, b) =>
      new Date(b.bp_order_master_updated_at).getTime() -
      new Date(a.bp_order_master_updated_at).getTime(),
  );
};

const employeeRecentTransactionsSlice = createSlice({
  name: 'employeeRecentTransactions',
  initialState,
  reducers: {
    setEmployeeRecentTransactionsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.loading = action.payload;
    },
    setEmployeeRecentTransactionsError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.error = action.payload;
    },
    setEmployeeRecentTransactions: (
      state,
      action: PayloadAction<PaginatedOrders>,
    ) => {
      state.recent = {
        ...state.recent,
        ...action.payload,
        docs: mergeOrders([], action.payload.docs || []),
      };
      state.lastFetchedAt = new Date().toISOString();
    },
    addEmployeeRecentTransactionsPage: (
      state,
      action: PayloadAction<PaginatedOrders>,
    ) => {
      state.recent = {
        ...state.recent,
        ...action.payload,
        docs: mergeOrders(state.recent.docs, action.payload.docs || []),
      };
      state.lastFetchedAt = new Date().toISOString();
    },
    clearEmployeeRecentTransactions: state => {
      state.recent = createInitialPage();
      state.error = null;
      state.lastFetchedAt = null;
    },
    upsertEmployeeRecentTransaction: (
      state,
      action: PayloadAction<OrderDoc>,
    ) => {
      state.recent.docs = mergeOrders(state.recent.docs, [action.payload]);
      state.lastFetchedAt = new Date().toISOString();
    },
  },
});

export const {
  setEmployeeRecentTransactionsLoading,
  setEmployeeRecentTransactionsError,
  setEmployeeRecentTransactions,
  addEmployeeRecentTransactionsPage,
  clearEmployeeRecentTransactions,
  upsertEmployeeRecentTransaction,
} = employeeRecentTransactionsSlice.actions;

export const selectEmployeeRecentTransactionsState = (state: RootState) =>
  state.employeeRecentTransactions;
export const selectEmployeeRecentTransactionsPage = (state: RootState) =>
  state.employeeRecentTransactions.recent;
export const selectEmployeeRecentTransactionsDocs = createSelector(
  [selectEmployeeRecentTransactionsPage],
  recent => recent.docs || [],
);

export default employeeRecentTransactionsSlice.reducer;
